import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useStartEventAgentRun } from '../api/useEventAgent';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called with the new run id once the agent run is dispatched. */
  onRunStarted: (runId: string) => void;
}

/**
 * "Auto-Map Event" — type a prediction-market event; the event-map agent
 * scrapes Kalshi + Polymarket and creates the pairs. Mirrors ManualPairDialog.
 */
export const AutoMapEventDialog = ({ open, onClose, onRunStarted }: Props) => {
  const [eventQuery, setEventQuery] = useState('');
  const startRun = useStartEventAgentRun();

  const handleClose = () => {
    if (startRun.isLoading) return;
    setEventQuery('');
    startRun.reset();
    onClose();
  };

  const handleSubmit = async () => {
    const q = eventQuery.trim();
    if (!q) return;
    const run = await startRun.mutateAsync(q);
    setEventQuery('');
    startRun.reset();
    onClose();
    onRunStarted(run._id);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Auto-Map Event</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            The agent scrapes every market for this event on Kalshi + Polymarket,
            creates the cross-platform pairs, and reports back which markets it
            could not pair. Enter an event name or a Kalshi/Polymarket event URL.
          </Typography>
          <TextField
            label="Event"
            placeholder="e.g. Cavaliers vs Knicks May 19"
            value={eventQuery}
            onChange={(e) => setEventQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit();
            }}
            autoFocus
            fullWidth
          />
          {startRun.isError && (
            <Alert severity="error">
              Failed to start the run. Check that prediction-manager can
              dispatch the agent Job.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={startRun.isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!eventQuery.trim() || startRun.isLoading}
          startIcon={
            startRun.isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {startRun.isLoading ? 'Starting…' : 'Map Event'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
